import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Search, Save, Check, CircleMinus, CircleCheck, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import Button from '../common/Button';
import FormControl, { SearchSelect } from '../common/FormControl';
import Modal from '../common/Modal';
import Pagination from '../common/Pagination';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmActive, confirmWarning } from '../common/confirm';
import { StatusBadge } from './AuditShared';
import { NOT_AUTHORISED_MSG, usePermission } from '../../contexts/PermissionContext';
import auditUniverseService, {
  UniverseDetail, UniverseInput, UniverseListItem, UniverseOptions, UniverseUnit,
} from '../../services/auditUniverseService';
import './AuditUniverse.css';

/**
 * Audits → Universe (GRC persona). BRs AUV-40…70 and AUA-10 in
 * docs/audit-planning-memorandum.html.
 *
 * The GRC team writes an audit universe as one unit — the list of auditable
 * units for one purpose — the way a program is written from procedures, gets it
 * approved through the status dot, and assigns it to one or more agencies for a
 * financial year. The assignment shows on each agency's Audit Plan screen.
 */

const errMsg = (e: any, fallback: string) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return String(d[0].msg).replace(/^Value error, /, '');
  return e?.message || fallback;
};

type Tab = 'Details' | 'Auditable units' | 'Agencies';

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

interface UniverseModalProps {
  isOpen: boolean;
  universeId: number | null;
  options: UniverseOptions;
  canModify: boolean;
  onClose: () => void;
  onChanged: (message?: string) => void;
}

const emptyDetails: UniverseInput = { universe_name: '', category: '', objective: '', description: '', owner_user_id: null };

const UniverseModal: React.FC<UniverseModalProps> = ({ isOpen, universeId, options, canModify, onClose, onChanged }) => {
  const [id, setId] = useState<number | null>(universeId);
  const [detail, setDetail] = useState<UniverseDetail | null>(null);
  const [tab, setTab] = useState<Tab>('Details');
  const [form, setForm] = useState<UniverseInput>(emptyDetails);
  const [units, setUnits] = useState<UniverseUnit[]>([]);
  const [fy, setFy] = useState<number>(options.current_fiscal_year);
  const [picked, setPicked] = useState<Record<number, string>>({}); // agency_id → plan due date ('' = none)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (uid: number) => {
    const d = await auditUniverseService.get(uid);
    setDetail(d);
    setForm({
      universe_name: d.universe_name, category: d.category, objective: d.objective ?? '',
      description: d.description ?? '', owner_user_id: d.owner_user_id ?? null,
    });
    setUnits(d.units);
    return d;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setId(universeId);
    setTab('Details');
    setError(null);
    setFy(options.current_fiscal_year);
    if (universeId) {
      load(universeId).catch(err => setError(errMsg(err, 'Could not load the universe.')));
    } else {
      setDetail(null);
      setForm(emptyDetails);
      setUnits([]);
    }
  }, [isOpen, universeId, options.current_fiscal_year, load]);

  useEffect(() => {
    if (!detail) { setPicked({}); return; }
    const next: Record<number, string> = {};
    detail.mandates.filter(m => m.fiscal_year === fy).forEach(m => { next[m.agency_id] = m.plan_due_date ?? ''; });
    setPicked(next);
  }, [detail, fy]);

  const state = (detail?.workflow_state ?? 'draft') as WorkflowState;
  const locked = state === 'approved' || state === 'submitted';
  const isActive = (detail?.is_active ?? 1) === 1;
  const editable = canModify && !locked;
  const lockNote = state === 'approved'
    ? 'Approved — read-only. Reopen from the status dot to edit.'
    : 'Waiting for approval — approve or reject from the status dot.';
  const fyMeta = options.fiscal_years.find(f => f.value === fy);

  const saveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editable || busy) return;
    setBusy(true); setError(null);
    try {
      const body = { ...form, universe_name: form.universe_name.trim() };
      if (id) {
        await auditUniverseService.update(id, body);
        await load(id);
        onChanged('Universe updated.');
      } else {
        const created = await auditUniverseService.create(body);
        setId(created.id);
        await load(created.id);
        setTab('Auditable units');
        onChanged(`Universe ${created.universe_code} created — now add its auditable units.`);
      }
    } catch (err) {
      setError(errMsg(err, 'Could not save the universe.'));
    } finally { setBusy(false); }
  };

  const toggleActive = async () => {
    if (!id) return;
    const ok = isActive
      ? await confirmWarning({ title: 'Mark inactive?', text: 'The universe is kept but no longer offered for new assignments.', confirmText: 'Inactive' })
      : await confirmActive({ title: 'Mark active?', text: 'The universe becomes available again.', confirmText: 'Active' });
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      await auditUniverseService.setStatus(id, isActive ? 0 : 1);
      await load(id);
      onChanged(isActive ? 'Universe marked inactive.' : 'Universe marked active.');
    } catch (err) {
      setError(errMsg(err, 'Could not change the status.'));
    } finally { setBusy(false); }
  };

  const setUnit = (i: number, patch: Partial<UniverseUnit>) =>
    setUnits(us => us.map((u, j) => (j === i ? { ...u, ...patch } : u)));
  const moveUnit = (i: number, d: -1 | 1) =>
    setUnits(us => {
      const j = i + d;
      if (j < 0 || j >= us.length) return us;
      const next = [...us];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  const unitsInvalid = units.some(u => u.unit_name.trim().length < 2 || !u.unit_type);

  const saveUnits = async () => {
    if (!id || !editable || busy || unitsInvalid) return;
    setBusy(true); setError(null);
    try {
      await auditUniverseService.saveUnits(id, units.map(u => ({ ...u, unit_name: u.unit_name.trim() })));
      await load(id);
      onChanged('Auditable units saved.');
    } catch (err) {
      setError(errMsg(err, 'Could not save the units.'));
    } finally { setBusy(false); }
  };

  const saveMandates = async () => {
    if (!id || busy) return;
    setBusy(true); setError(null);
    try {
      await auditUniverseService.saveMandates(id, fy,
        Object.entries(picked).map(([aid, due]) => ({ agency_id: Number(aid), plan_due_date: due || null })));
      await load(id);
      onChanged('Agency assignment saved.');
    } catch (err) {
      setError(errMsg(err, 'Could not save the agency assignment.'));
    } finally { setBusy(false); }
  };

  const canAssign = canModify && state === 'approved' && isActive;
  const tabs: Tab[] = ['Details', 'Auditable units', 'Agencies'];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" data-testid="MDL_universe">
      <Modal.Header>
        <span className="universe-modal-title" title={detail?.universe_name}>
          {detail ? `${detail.universe_code} · ${detail.universe_name}` : 'Create audit universe'}
        </span>
      </Modal.Header>
      <Modal.Body>
        <div className="app-modal-tabs">
          {tabs.map(t => (
            <button
              key={t}
              type="button"
              className={`app-modal-tab${tab === t ? ' app-modal-tab--active' : ''}`}
              onClick={() => setTab(t)}
              disabled={!id && t !== 'Details'}
              data-testid={`TAB_universe_${t.toLowerCase().replace(/\s+/g, '_')}`}
            >
              {t}
              {t === 'Auditable units' && id && <span className="universe-tab-count">{detail?.units.length ?? 0}</span>}
              {t === 'Agencies' && id && <span className="universe-tab-count">{detail?.mandates.length ?? 0}</span>}
            </button>
          ))}
        </div>

        {error && <div className="warning-text" role="alert" style={{ margin: '12px 0 0' }} data-testid="LBL_universe_error">{error}</div>}

        {tab === 'Details' && (
          <form id="universe-form" onSubmit={saveDetails} noValidate className="grid grid-cols-2 gap-x-6 gap-y-4 universe-tab-body">
            <fieldset disabled={!editable} className="contents">
              <FormControl.Input
                label="Universe name"
                required
                maxLength={200}
                placeholder="e.g. IT and cybersecurity universe"
                value={form.universe_name}
                onChange={e => setForm(f => ({ ...f, universe_name: e.target.value }))}
                className="col-span-2"
                data-testid="INP_universe_name"
              />
              <FormControl.Select
                label="Category"
                required
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                data-testid="SEL_universe_category"
              >
                <option value="">Select…</option>
                {options.categories.map(c => <option key={c} value={c}>{c}</option>)}
              </FormControl.Select>
              <SearchSelect
                label="Owner"
                options={options.users.map(u => ({ value: u.id, label: u.name }))}
                value={form.owner_user_id ?? null}
                onChange={v => setForm(f => ({ ...f, owner_user_id: v == null ? null : Number(v) }))}
                clearable
                placeholder="Choose the owner"
                disabled={!editable}
                data-testid="SEL_universe_owner"
              />
              <FormControl.Textarea
                label="Objective"
                rows={2}
                maxLength={4000}
                placeholder="What this universe exists to give assurance on"
                value={form.objective ?? ''}
                onChange={e => setForm(f => ({ ...f, objective: e.target.value }))}
                className="col-span-2"
                data-testid="INP_universe_objective"
              />
              <FormControl.Textarea
                label="Description"
                rows={3}
                maxLength={4000}
                value={form.description ?? ''}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="col-span-2"
                data-testid="INP_universe_description"
              />
            </fieldset>
          </form>
        )}

        {tab === 'Auditable units' && (
          <div className="universe-tab-body">
            <p className="universe-hint">
              The things that can be audited under this universe, in order. Each may carry a recommended
              GRC-Common program, which the agency sees as the default when planning.
            </p>
            {units.length === 0 ? (
              <NoDataFound title="No auditable units yet" message={editable ? 'Use “Add unit” to build the universe.' : 'This universe has no units.'} />
            ) : (
              <div className="table-list">
                <div className="table-list-scroll">
                  <table className="table-list-table universe-units-table">
                    <thead>
                      <tr>
                        <th className="table-list-th table-list-cell--sno">#</th>
                        <th className="table-list-th">Auditable unit</th>
                        <th className="table-list-th">Type</th>
                        <th className="table-list-th">Recommended program</th>
                        {editable && <th className="table-list-th table-list-th--right" aria-label="Reorder or remove" />}
                      </tr>
                    </thead>
                    <tbody>
                      {units.map((u, i) => (
                        <tr key={u.id ?? `new-${i}`} className="table-list-row" data-testid={`ROW_universe_unit_${i}`}>
                          <td className="table-list-cell table-list-cell--sno">{i + 1}</td>
                          <td className="table-list-cell">
                            {editable ? (
                              <FormControl.Input
                                value={u.unit_name}
                                maxLength={200}
                                placeholder="e.g. Admissions and fee collection"
                                onChange={e => setUnit(i, { unit_name: e.target.value })}
                                error={u.unit_name.trim().length < 2 ? 'Required' : undefined}
                                data-testid={`INP_universe_unit_name_${i}`}
                              />
                            ) : <span className="table-list-cell-title">{u.unit_name}</span>}
                          </td>
                          <td className="table-list-cell">
                            {editable ? (
                              <FormControl.Select
                                value={u.unit_type}
                                onChange={e => setUnit(i, { unit_type: e.target.value })}
                                data-testid={`SEL_universe_unit_type_${i}`}
                              >
                                <option value="">Select…</option>
                                {options.unit_types.map(t => <option key={t} value={t}>{t}</option>)}
                              </FormControl.Select>
                            ) : u.unit_type}
                          </td>
                          <td className="table-list-cell">
                            {editable ? (
                              <SearchSelect
                                options={options.programs.map(p => ({ value: p.id, label: p.name }))}
                                value={u.recommended_program_id ?? null}
                                onChange={v => setUnit(i, { recommended_program_id: v == null ? null : Number(v) })}
                                clearable
                                clearLabel="— None —"
                                placeholder="None"
                                data-testid={`SEL_universe_unit_program_${i}`}
                              />
                            ) : (u.recommended_program_name || '—')}
                          </td>
                          {editable && (
                            <td className="table-list-cell table-list-cell--actions universe-unit-actions">
                              <button type="button" className="btn-primary-outline-none" title="Move up" onClick={() => moveUnit(i, -1)} disabled={i === 0} data-testid={`BTN_universe_unit_up_${i}`}><ArrowUp size={14} /></button>
                              <button type="button" className="btn-primary-outline-none" title="Move down" onClick={() => moveUnit(i, 1)} disabled={i === units.length - 1} data-testid={`BTN_universe_unit_down_${i}`}><ArrowDown size={14} /></button>
                              <button type="button" className="btn-danger-outline-none" title="Remove" onClick={() => setUnits(us => us.filter((_, j) => j !== i))} data-testid={`BTN_universe_unit_remove_${i}`}><Trash2 size={14} /></button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {editable && (
              <div style={{ marginTop: 10 }}>
                <Button
                  variant="secondary-outline"
                  icon={Plus}
                  onClick={() => setUnits(us => [...us, { unit_name: '', unit_type: '', recommended_program_id: null }])}
                  data-testid="BTN_universe_unit_add"
                >
                  Add unit
                </Button>
              </div>
            )}
          </div>
        )}

        {tab === 'Agencies' && (
          <div className="universe-tab-body">
            <p className="universe-hint">
              Agencies that hold this universe for the year. An agency may hold several universes, and a universe
              may be held by several agencies. How the universe is covered over the year is then the agency’s own plan.
            </p>
            <div className="universe-fy-row">
              <FormControl.Select
                label="Financial year"
                value={fy}
                onChange={e => setFy(Number(e.target.value))}
                data-testid="SEL_universe_fy"
              >
                {options.fiscal_years.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </FormControl.Select>
              {!canAssign && (
                <span className="universe-hint" style={{ margin: 0 }}>
                  {state !== 'approved' ? 'Approve the universe before assigning it to agencies.' : !isActive ? 'An inactive universe cannot be assigned.' : NOT_AUTHORISED_MSG}
                </span>
              )}
            </div>
            {options.agencies.length === 0 ? (
              <NoDataFound title="No approved agencies" message="Approve an agency on the Agencies tab first." />
            ) : (
              <div className="table-list">
                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <thead>
                      <tr>
                        <th className="table-list-th" style={{ width: 48 }} aria-label="Assigned" />
                        <th className="table-list-th">Agency</th>
                        <th className="table-list-th">Type</th>
                        <th className="table-list-th">Plan due by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {options.agencies.map(a => {
                        const on = a.id in picked;
                        return (
                          <tr key={a.id} className="table-list-row" data-testid={`ROW_universe_agency_${a.id}`}>
                            <td className="table-list-cell">
                              <input
                                type="checkbox"
                                checked={on}
                                disabled={!canAssign}
                                aria-label={`Assign to ${a.name}`}
                                onChange={() => setPicked(p => {
                                  const next = { ...p };
                                  if (on) delete next[a.id]; else next[a.id] = '';
                                  return next;
                                })}
                                data-testid={`CHK_universe_agency_${a.id}`}
                              />
                            </td>
                            <td className="table-list-cell"><span className="table-list-cell-title">{a.name}</span></td>
                            <td className="table-list-cell">{a.agency_kind}</td>
                            <td className="table-list-cell" style={{ maxWidth: 200 }}>
                              {on ? (
                                <FormControl.Input
                                  type="date"
                                  value={picked[a.id]}
                                  max={fyMeta?.end}
                                  disabled={!canAssign}
                                  onChange={e => setPicked(p => ({ ...p, [a.id]: e.target.value }))}
                                  data-testid={`INP_universe_agency_due_${a.id}`}
                                />
                              ) : <span className="text-muted text-xs">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className={tab === 'Details' && id && editable ? 'app-modal-footer--split' : ''}>
        {tab === 'Details' && (locked ? (
          <span className="universe-readonly-note" data-testid="LBL_universe_readonly">{lockNote}</span>
        ) : (
          <>
            {id && editable && (
              <Button
                variant={isActive ? 'inactive-outline' : 'active-outline'}
                icon={isActive ? CircleMinus : CircleCheck}
                type="button"
                onClick={toggleActive}
                disabled={busy}
                data-testid="BTN_universe_toggle_active"
              >
                {isActive ? 'Inactive' : 'Active'}
              </Button>
            )}
            <Button
              type="submit"
              form="universe-form"
              variant={id ? 'update' : 'fill'}
              icon={id ? Check : Save}
              disabled={!editable || busy || form.universe_name.trim().length < 3 || !form.category}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
              data-testid="BTN_universe_submit"
            >
              {busy ? 'Saving...' : id ? 'Update' : 'Save'}
            </Button>
          </>
        ))}
        {tab === 'Auditable units' && (locked ? (
          <span className="universe-readonly-note">{lockNote}</span>
        ) : (
          <Button variant="fill" icon={Save} onClick={saveUnits} disabled={!editable || busy || unitsInvalid} data-testid="BTN_universe_units_save">
            {busy ? 'Saving...' : 'Save units'}
          </Button>
        ))}
        {tab === 'Agencies' && (
          <Button variant="fill" icon={Save} onClick={saveMandates} disabled={!canAssign || busy} data-testid="BTN_universe_agencies_save">
            {busy ? 'Saving...' : 'Save assignment'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

type StatusFilter = 'active' | 'inactive';

const AuditUniverse: React.FC = () => {
  const { allowed: canView, loading: viewLoading } = usePermission('ADT_PROGRAMS', 'view');
  const { allowed: canModify, loading: permLoading } = usePermission('ADT_PROGRAMS', 'view+modify');
  const [items, setItems] = useState<UniverseListItem[] | null>(null);
  const [options, setOptions] = useState<UniverseOptions | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modal, setModal] = useState<{ id: number | null } | null>(null);
  const [wfId, setWfId] = useState<number | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const reload = useCallback(async () => {
    try {
      setItems(await auditUniverseService.list());
    } catch (err) {
      setItems([]);
      setToast({ type: 'error', message: errMsg(err, 'Could not load universes.') });
    }
  }, []);

  useEffect(() => {
    if (viewLoading || !canView) return;
    reload();
    auditUniverseService.options().then(setOptions)
      .catch(err => setToast({ type: 'error', message: errMsg(err, 'Could not load form options.') }));
  }, [viewLoading, canView, reload]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items ?? []).filter(u => {
      if (statusFilter === 'active' && u.is_active !== 1) return false;
      if (statusFilter === 'inactive' && u.is_active !== 0) return false;
      if (q && !`${u.universe_code} ${u.universe_name} ${u.category} ${u.agency_names}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, statusFilter]);

  useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);
  const paginated = visible.slice((page - 1) * pageSize, page * pageSize);

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to 'ADT_PROGRAMS'." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full min-h-[500px]">
      <div className="netbody-header">
        <StatusSwitch
          value={statusFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
          onChange={next => setStatusFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
          noun="universes"
          testId="SEL_universe_status_filter"
        />
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.Input
            type="text"
            placeholder="Search universes…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_universe_search"
          />
          <Button
            variant="fill"
            icon={Plus}
            onClick={() => setModal({ id: null })}
            disabled={permLoading || !canModify || !options}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            data-testid="BTN_universe_create"
          >
            Create
          </Button>
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--dot" aria-label="Workflow status" />
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Code</th>
                    <th className="table-list-th">Universe</th>
                    <th className="table-list-th">Category</th>
                    <th className="table-list-th table-list-cell--right">Units</th>
                    <th className="table-list-th">Assigned to</th>
                    <th className="table-list-th table-list-cell--status">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items === null && [0, 1, 2].map(i => (
                    <tr key={i}><td colSpan={8} className="table-list-cell"><div className="universe-skeleton" /></td></tr>
                  ))}
                  {items !== null && visible.length === 0 && (
                    <tr>
                      <td colSpan={8} className="table-list-cell">
                        <NoDataFound title="No Universes Found" message='Click "Create" to write the first audit universe.' />
                      </td>
                    </tr>
                  )}
                  {paginated.map((u, idx) => (
                    <tr
                      key={u.id}
                      className="table-list-row"
                      onClick={() => options && setModal({ id: u.id })}
                      data-testid={`BTN_universe_row_${u.id}_open`}
                    >
                      <td
                        className="table-list-cell table-list-cell--dot"
                        onClick={e => { e.stopPropagation(); setWfId(u.id); }}
                        title="Workflow — view timeline & act"
                        data-testid={`BTN_universe_row_${u.id}_workflow`}
                      >
                        <ApprovalDot state={(u.workflow_state || 'draft') as WorkflowState} onClick={() => setWfId(u.id)} title="Workflow — view timeline & act" />
                      </td>
                      <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                      <td className="table-list-cell"><span className="cell-code">{u.universe_code}</span></td>
                      <td className="table-list-cell">
                        <span className="table-list-cell-title">{u.universe_name}</span>
                        {u.objective && <div className="universe-row-sub">{u.objective}</div>}
                      </td>
                      <td className="table-list-cell">{u.category}</td>
                      <td className="table-list-cell table-list-cell--right">{u.unit_count}</td>
                      <td className="table-list-cell">{u.agency_names || <span className="text-muted text-xs">Not assigned</span>}</td>
                      <td className="table-list-cell table-list-cell--status">
                        <StatusBadge status={u.is_approved === 1 ? 'Approved' : u.is_active === 0 ? 'Inactive' : 'Active'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={visible.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="universes"
            />
          </div>
        </div>
      </div>

      {options && modal && (
        <UniverseModal
          isOpen={!!modal}
          universeId={modal.id}
          options={options}
          canModify={canModify}
          onClose={() => setModal(null)}
          onChanged={message => { reload(); if (message) setToast({ type: 'success', message }); }}
        />
      )}
      <WorkflowModal isOpen={wfId != null} onClose={() => setWfId(null)} kind="adt_universe" id={wfId} onChanged={reload} />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default AuditUniverse;
